import { useState } from "react"
import { Icon } from "@/components/Icon"
import { safeHref } from "@/lib/safe-url"
import type { LegalCitation } from "../hooks/useLegalResearchStream"

interface QaCitationsProps {
  citations: LegalCitation[]
  evidence: Record<string, unknown>
}

interface EvidenceMatch {
  text: string
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? (value.filter((v) => v && typeof v === "object") as Record<string, unknown>[])
    : []
}

const EVIDENCE_KEY_BY_SOURCE: Record<string, string> = {
  statutory: "statutory_acts",
  blended: "blended",
  graph_chunk: "graph_chunks",
  web: "web_evidence",
}

function findEvidenceMatch(
  citation: LegalCitation,
  evidence: Record<string, unknown>
): EvidenceMatch | null {
  const key = EVIDENCE_KEY_BY_SOURCE[citation.source_type]
  if (!key) return null
  const items = asRecordArray(evidence[key])

  if (citation.source_type === "web") {
    const match = items.find((item) => item.title === citation.reference_number)
    const snippet = match && typeof match.snippet === "string" ? match.snippet : ""
    return snippet ? { text: snippet } : null
  }

  const match = items.find((item) => item.reference_number === citation.reference_number)
  if (!match) return null
  const textField = citation.source_type === "graph_chunk" ? match.text : match.snippet
  return typeof textField === "string" && textField ? { text: textField } : null
}

export function QaCitations({ citations, evidence }: QaCitationsProps) {
  const [openKey, setOpenKey] = useState<string | null>(null)

  if (citations.length === 0) return null

  return (
    <div className="qa-sources">
      <h4>Sources</h4>
      {citations.map((citation, i) => {
        const match = findEvidenceMatch(citation, evidence)
        const citationKey = `${citation.source_type}-${citation.reference_number}`
        const isOpen = openKey === citationKey
        return (
          <div className="qa-src" key={`${citation.source_type}-${citation.reference_number}-${i}`}>
            <span className="qa-src__n">{i + 1}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="qa-src__t">{citation.title || citation.label}</div>
              <div className="qa-src__m">
                {citation.reference_number || citation.source_type}
                {safeHref(citation.url) && (
                  <a href={safeHref(citation.url)} target="_blank" rel="noreferrer" style={{ marginLeft: 8 }}>
                    <Icon name="ext" size={12} />
                  </a>
                )}
              </div>
              {match && (
                <button
                  className="btn btn--quiet btn--sm"
                  style={{ marginTop: 6, padding: 0, height: "auto" }}
                  onClick={() => setOpenKey(isOpen ? null : citationKey)}
                  aria-expanded={isOpen}
                  aria-controls={`evidence-text-${i}`}
                >
                  <Icon name={isOpen ? "chevdown" : "chevright"} size={12} />
                  {isOpen ? "Hide source text" : "Show source text"}
                </button>
              )}
              {isOpen && match && (
                <p className="qa-src__q" id={`evidence-text-${i}`}>
                  {match.text}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
